"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { Mail, Phone, MapPin, Send, Sparkles, Loader2 } from "lucide-react"
import { propertyTypeAPI } from "@/lib/api"

export function InteriorContact() {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        propertyType: "",
        message: "",
    })

    const [propertyTypes, setPropertyTypes] = useState<any[]>([])
    const [loadingPropertyTypes, setLoadingPropertyTypes] = useState(true)

    useEffect(() => {
        const fetchPropertyTypes = async () => {
            try {
                setLoadingPropertyTypes(true)
                const resp = await propertyTypeAPI.getAllWeb('property_type')
                const raw = (resp as any)?.data
                const list = (raw?.data ?? raw?.items ?? raw?.results ?? raw) as any[]
                setPropertyTypes(Array.isArray(list) ? list : [])
            } catch (error) {
                console.error('Error fetching property types:', error)
                setPropertyTypes([])
            } finally {
                setLoadingPropertyTypes(false)
            }
        }

        fetchPropertyTypes()
    }, [])

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        console.log("Form submitted:", formData)
    }

    return (
        <section className="py-24 lg:py-32 bg-gradient-to-b from-background via-background to-muted/20 relative overflow-hidden">
            {/* Decorative background elements */}
            <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>

            <div className="container mx-auto px-4 lg:px-8 relative z-10">
                {/* Header Section */}
                <div className="text-center mb-16 max-w-3xl mx-auto">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6 text-sm font-medium">
                        <Sparkles className="h-4 w-4" />
                        <span>Start Your Design Journey</span>
                    </div>
                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground mb-6 text-balance">
                        Let's Create Something
                        <span className="block bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                            Beautiful Together
                        </span>
                    </h2>
                    <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                        Ready to transform your space? Schedule a consultation with our design team to discuss your vision and
                        explore the possibilities.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                    {/* Contact Information Sidebar */}
                    <div className="lg:col-span-1 space-y-6">
                        <div className="bg-gradient-to-br from-background to-muted/50 backdrop-blur-sm border border-border/50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                            <h3 className="text-2xl font-heading font-bold text-foreground mb-8 flex items-center gap-2">
                                <div className="w-1 h-8 bg-primary rounded-full"></div>
                                Get in Touch
                            </h3>
                            <div className="space-y-6">
                                <a
                                    href="tel:+971524227546"
                                    className="flex items-start gap-4 group p-4 rounded-xl hover:bg-muted/50 transition-all duration-200"
                                >
                                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 group-hover:from-primary/30 group-hover:to-primary/20 transition-all duration-300 flex items-center justify-center flex-shrink-0 shadow-sm">
                                        <Phone className="h-6 w-6 text-primary" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-semibold text-foreground mb-1 text-base">Phone</p>
                                        <p className="text-muted-foreground text-sm group-hover:text-primary transition-colors">+971 52 422 7546</p>
                                    </div>
                                </a>

                                <a
                                    href="mailto:info@18bricksholidayhomes.ae"
                                    className="flex items-start gap-4 group p-4 rounded-xl hover:bg-muted/50 transition-all duration-200"
                                >
                                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 group-hover:from-primary/30 group-hover:to-primary/20 transition-all duration-300 flex items-center justify-center flex-shrink-0 shadow-sm">
                                        <Mail className="h-6 w-6 text-primary" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-semibold text-foreground mb-1 text-base">Email</p>
                                        <p className="text-muted-foreground text-sm group-hover:text-primary transition-colors break-all">info@18bricksholidayhomes.ae</p>
                                    </div>
                                </a>

                                <div className="flex items-start gap-4 group p-4 rounded-xl hover:bg-muted/50 transition-all duration-200">
                                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 group-hover:from-primary/30 group-hover:to-primary/20 transition-all duration-300 flex items-center justify-center flex-shrink-0 shadow-sm">
                                        <MapPin className="h-6 w-6 text-primary" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-semibold text-foreground mb-1 text-base">Studio</p>
                                        <p className="text-muted-foreground text-sm">
                                            Quarter A, 102, Expo City Dubai,
                                            <br />
                                            8 Mangrove Avenue, Dubai, UAE

                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact Form */}
                    <div className="lg:col-span-2">
                        <div className="bg-gradient-to-br from-background via-background to-muted/30 backdrop-blur-sm border border-border/50 rounded-2xl p-8 md:p-10 shadow-xl hover:shadow-2xl transition-all duration-300">
                            <div className="mb-8">
                                <h3 className="text-3xl font-heading font-bold text-foreground mb-2 flex items-center gap-2">
                                    <div className="w-1 h-8 bg-primary rounded-full"></div>
                                    Send us a Message
                                </h3>
                                <p className="text-muted-foreground text-sm">Fill out the form below and we'll get back to you within 24 hours.</p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2 md:col-span-2">
                                        <Label htmlFor="name" className="text-foreground font-semibold text-sm">
                                            Full Name *
                                        </Label>
                                        <Input
                                            id="name"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="h-12 border-2 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 bg-background/50"
                                            placeholder="John Doe"
                                            required
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="email" className="text-foreground font-semibold text-sm">
                                            Email Address *
                                        </Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            className="h-12 border-2 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 bg-background/50"
                                            placeholder="john@example.com"
                                            required
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="phone" className="text-foreground font-semibold text-sm">
                                            Phone Number
                                        </Label>
                                        <Input
                                            id="phone"
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            className="h-12 border-2 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 bg-background/50"
                                            placeholder="+1 (555) 000-0000"
                                        />
                                    </div>

                                    <div className="space-y-2 md:col-span-2">
                                        <Label htmlFor="propertyType" className="text-foreground font-semibold text-sm">
                                            Property Type
                                        </Label>
                                        <Select
                                            value={formData.propertyType}
                                            onValueChange={(value) => setFormData({ ...formData, propertyType: value })}
                                            disabled={loadingPropertyTypes}
                                        >
                                            <SelectTrigger className="h-12 border-2 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 bg-background/50">
                                                <SelectValue placeholder={loadingPropertyTypes ? "Loading property types..." : "Select your property type"} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {loadingPropertyTypes ? (
                                                    <div className="flex items-center justify-center p-4">
                                                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                                    </div>
                                                ) : propertyTypes.length > 0 ? (
                                                    propertyTypes?.map((type) => (
                                                        <SelectItem key={type._id || type.id} value={type._id || type.id}>
                                                            {type.name}
                                                        </SelectItem>
                                                    ))
                                                ) : (
                                                    <SelectItem value="none" disabled>No property types available</SelectItem>
                                                )}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2 md:col-span-2">
                                        <Label htmlFor="message" className="text-foreground font-semibold text-sm">
                                            Tell Us About Your Project *
                                        </Label>
                                        <Textarea
                                            id="message"
                                            value={formData.message}
                                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                            rows={6}
                                            className="border-2 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 resize-none bg-background/50"
                                            placeholder="Describe your vision, timeline, budget, and any specific requirements..."
                                            required
                                        />
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    size="lg"
                                    className="w-full h-14 bg-gradient-to-r from-primary to-primary/90 text-primary-foreground hover:from-primary/90 hover:to-primary shadow-lg hover:shadow-xl transition-all duration-300 group text-base font-semibold"
                                >
                                    <Send className="mr-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                                    Schedule Consultation
                                </Button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
